import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle, 
  Clock, Trophy, AlertTriangle, RotateCcw, Play, Lock, ShieldAlert, Download
} from 'lucide-react';
import { useGenericTimeTracking } from '../../hooks/useTimeTracking';

const API = API_BASE;
const OVERRIDE_CODE = "8098242463";

export default function FinalExam() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examStarted, setExamStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(2 * 60 * 60); // 2 hours in seconds
  const [submitting, setSubmitting] = useState(false);
  const [errorState, setErrorState] = useState(null);
  
  // Blocked state management
  const [isBlocked, setIsBlocked] = useState(false);
  const [overrideCode, setOverrideCode] = useState('');
  const [overrideError, setOverrideError] = useState('');
  const [downloadingCert, setDownloadingCert] = useState(false);

  // Track time spent on the final exam — only while actively taking it
  useGenericTimeTracking('final_exam', null, examStarted && !showResults);

  const lang = user?.training_language || 'en';
  const t = (en, es) => lang === 'es' ? es : en;

  // Function to download PDF certificate
  const handleDownloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const response = await axios.get(`${API}/training/certificate/download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificado_Academia_${user?.full_name?.replace(/\s+/g, '_') || 'Trainee'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('Certificate downloaded!', '¡Certificado descargado!'));
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error(t('Error downloading certificate', 'Error al descargar el certificado'));
    } finally {
      setDownloadingCert(false);
    }
  };

  // Check if user is blocked on mount
  useEffect(() => {
    const blocked = localStorage.getItem(`exam_blocked_${user?.id}`);
    if (blocked === 'true') {
      setIsBlocked(true);
    }
  }, [user?.id]);

  // Fetch the Final Exam quiz from backend
  useEffect(() => {
    if (!isBlocked) {
      fetchFinalExam();
    } else {
      setLoading(false);
    }
  }, [isBlocked]);

  const fetchFinalExam = async () => {
    try {
      // Use the dedicated final-exam endpoint
      const response = await axios.get(`${API}/training/final-exam`);
      setQuiz(response.data);
      
      // Check if blocked based on block_reason from backend
      if (response.data.block_reason) {
        setErrorState(response.data.block_reason);
      } else {
        setErrorState(null);
      }
    } catch (error) {
      console.error('Error fetching final exam:', error);
      const detail = error.response?.data?.detail || '';
      
      if (detail.includes('Complete all modules')) {
        setErrorState('modules_incomplete');
      } else if (detail.includes('Already certified')) {
        setErrorState('already_certified');
      } else if (detail.includes('already attempted') || detail.includes('Max attempts')) {
        setErrorState('max_attempts_reached');
      } else if (error.response?.status === 404 || detail.includes('not configured')) {
        setErrorState('not_configured');
      } else {
        toast.error(t('Error loading final exam', 'Error al cargar el examen final'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Timer
  useEffect(() => {
    if (!examStarted || showResults) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, showResults]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (index) => {
    setAnswers({ ...answers, [currentQuestion]: index });
  };

  const handleNextQuestion = () => {
    if (currentQuestion < (quiz?.questions?.length || 0) - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    // Convert answers object to array format expected by backend
    const answersArray = [];
    const totalQuestions = quiz?.questions?.length || 0;
    
    for (let i = 0; i < totalQuestions; i++) {
      answersArray.push(answers[i] !== undefined ? answers[i] : -1);
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/training/final-exam/submit`, {
        answers: answersArray
      });
      setResult(response.data);
      setShowResults(true);
      
      if (response.data.passed) {
        toast.success(t('Congratulations! You passed the final exam!', '¡Felicidades! ¡Aprobaste el examen final!'));
        // Clear any blocked state on pass
        localStorage.removeItem(`exam_blocked_${user?.id}`);
      } else {
        // Block the user if they fail
        localStorage.setItem(`exam_blocked_${user?.id}`, 'true');
        setIsBlocked(true);
        toast.error(t('You did not pass. Platform is now locked. Contact your administrator for an override code.', 
          'No aprobaste. La plataforma está bloqueada. Contacta a tu administrador para un código de desbloqueo.'));
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error(error.response?.data?.detail || t('Error submitting exam', 'Error al enviar el examen'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverrideSubmit = () => {
    if (overrideCode === OVERRIDE_CODE) {
      localStorage.removeItem(`exam_blocked_${user?.id}`);
      setIsBlocked(false);
      setOverrideCode('');
      setOverrideError('');
      toast.success(t('Platform unlocked! You can now retake the exam.', '¡Plataforma desbloqueada! Ahora puedes volver a tomar el examen.'));
      fetchFinalExam();
    } else {
      setOverrideError(t('Invalid override code. Please contact your administrator.', 'Código inválido. Por favor contacta a tu administrador.'));
    }
  };

  const restartExam = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResult(null);
    setTimeLeft(2 * 60 * 60);
    setExamStarted(true);
    fetchFinalExam();
  };

  // Show blocked screen if user failed and is blocked
  if (isBlocked) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-500 bg-gradient-to-br from-red-50 to-orange-50">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="h-20 w-20 mx-auto text-red-500 mb-4" />
            <h2 className="text-3xl font-bold text-red-700 mb-2">
              {t('Platform Locked', 'Plataforma Bloqueada')}
            </h2>
            <p className="text-red-600 mb-6">
              {t(
                'You did not pass the final certification exam with the required 80%. Your training platform access has been locked. Please contact your administrator to receive an override code to unlock and retake the exam.',
                'No aprobaste el examen final de certificación con el 80% requerido. Tu acceso a la plataforma de entrenamiento ha sido bloqueado. Por favor contacta a tu administrador para recibir un código de desbloqueo y volver a tomar el examen.'
              )}
            </p>
            
            <div className="space-y-4 max-w-sm mx-auto">
              <div className="space-y-2">
                <Label htmlFor="override-code" className="text-left block">
                  {t('Administrator Override Code', 'Código de Desbloqueo del Administrador')}
                </Label>
                <Input
                  id="override-code"
                  type="text"
                  placeholder={t('Enter override code', 'Ingresa el código de desbloqueo')}
                  value={overrideCode}
                  onChange={(e) => {
                    setOverrideCode(e.target.value);
                    setOverrideError('');
                  }}
                  className="text-center text-lg tracking-widest"
                />
                {overrideError && (
                  <p className="text-sm text-red-600">{overrideError}</p>
                )}
              </div>
              
              <Button 
                onClick={handleOverrideSubmit}
                disabled={!overrideCode}
                className="w-full"
              >
                <Lock className="h-4 w-4 mr-2" />
                {t('Unlock Platform', 'Desbloquear Plataforma')}
              </Button>
            </div>
            
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                {t(
                  'Note: The administrator will provide the override code only after reviewing your training progress and discussing areas for improvement.',
                  'Nota: El administrador proporcionará el código de desbloqueo solo después de revisar tu progreso de entrenamiento y discutir áreas de mejora.'
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!quiz) {
    // Handle different error states
    if (errorState === 'modules_incomplete') {
      return (
        <div className="p-6 max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back to Training', 'Volver a Capacitación')}
          </Button>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-blue-500 mb-4" />
              <h2 className="text-2xl font-bold text-blue-700">
                {t('Complete Training First', 'Completa la Capacitación Primero')}
              </h2>
              <p className="text-blue-600 mt-2">
                {t('You must complete all training modules before taking the final exam.', 
                   'Debes completar todos los módulos de capacitación antes de tomar el examen final.')}
              </p>
              <Button onClick={() => navigate('/training')} className="mt-6">
                {t('Go to Training', 'Ir a Capacitación')}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (errorState === 'already_certified') {
      return (
        <div className="p-6 max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back to Training', 'Volver a Capacitación')}
          </Button>
          
          <Card className="border-green-500 bg-green-50">
            <CardContent className="p-8 text-center space-y-4">
              <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-2xl font-bold text-green-700">
                🎉 {t('Already Certified!', '¡Ya Estás Certificado!')}
              </h2>
              <p className="text-green-600 mt-2">
                {t('Congratulations! You have already passed the certification exam.', 
                   '¡Felicidades! Ya has aprobado el examen de certificación.')}
              </p>
              
              {/* Download Certificate Button */}
              <Button 
                onClick={handleDownloadCertificate}
                disabled={downloadingCert}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
                size="lg"
                data-testid="download-certificate-btn"
              >
                {downloadingCert ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('Generating...', 'Generando...')}
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    {t('Download Certificate (PDF)', 'Descargar Certificado (PDF)')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (errorState === 'already_attempted') {
      return (
        <div className="p-6 max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back to Training', 'Volver a Capacitación')}
          </Button>
          
          <Card className="border-red-500 bg-red-50">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-2xl font-bold text-red-700">
                {t('Exam Already Attempted', 'Examen Ya Intentado')}
              </h2>
              <p className="text-red-600 mt-2">
                {t('You have already used your attempt for the final exam. Please contact your administrator for assistance.', 
                   'Ya has usado tu intento para el examen final. Por favor contacta a tu administrador para asistencia.')}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Default: not configured
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
        
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
            <h2 className="text-2xl font-bold text-amber-700">
              {t('Final Exam Not Available', 'Examen Final No Disponible')}
            </h2>
            <p className="text-amber-600 mt-2">
              {t('The final exam has not been configured yet. Please contact your administrator.', 
                 'El examen final no ha sido configurado todavía. Por favor contacta a tu administrador.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already passed
  if (quiz.passed && !examStarted) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
        
        <Card className="border-green-500 bg-green-50">
          <CardContent className="p-8 text-center space-y-4">
            <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-green-700">
              🎉 {t('You Have Already Passed!', '¡Ya Has Aprobado!')}
            </h2>
            <p className="text-green-600 mt-2">
              {t('Congratulations! You have successfully completed the Training Academy certification.', 
                 '¡Felicidades! Has completado exitosamente la certificación de la Academia de Capacitación.')}
            </p>
            
            {/* Download Certificate Button */}
            <Button 
              onClick={handleDownloadCertificate}
              disabled={downloadingCert}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
              size="lg"
              data-testid="download-certificate-btn"
            >
              {downloadingCert ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('Generating...', 'Generando...')}
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  {t('Download Certificate (PDF)', 'Descargar Certificado (PDF)')}
                </>
              )}
            </Button>
            
            <Button variant="outline" onClick={() => navigate('/training')} className="w-full">
              {t('Back to Training', 'Volver a Capacitación')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No more attempts
  if (errorState === 'max_attempts_reached' || (!quiz?.can_attempt && quiz?.block_reason === 'max_attempts_reached' && !examStarted)) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
        
        <Card className="border-red-500 bg-red-50">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-red-700">
              {t('No More Attempts', 'Sin Más Intentos')}
            </h2>
            <p className="text-red-600 mt-2">
              {t(
                `You have used all ${quiz?.max_attempts || 2} attempts for the final exam. Please contact your administrator.`,
                `Has usado los ${quiz?.max_attempts || 2} intentos para el examen final. Por favor contacta a tu administrador.`
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Start screen
  if (!examStarted) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/training')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>

        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              🎓 {lang === 'es' ? quiz.title_es : quiz.title_en}
            </CardTitle>
            <CardDescription>
              {lang === 'es' ? quiz.description_es : quiz.description_en}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4 bg-white/70 rounded-lg border">
                <p className="text-2xl font-bold">{quiz.questions?.length || 0}</p>
                <p className="text-sm text-muted-foreground">{t('Questions', 'Preguntas')}</p>
              </div>
              <div className="p-4 bg-white/70 rounded-lg border">
                <p className="text-2xl font-bold">{quiz.passing_score || 80}%</p>
                <p className="text-sm text-muted-foreground">{t('To Pass', 'Para Aprobar')}</p>
              </div>
              <div className="p-4 bg-white/70 rounded-lg border">
                <p className="text-2xl font-bold">3 hrs</p>
                <p className="text-sm text-muted-foreground">{t('Time Limit', 'Tiempo')}</p>
              </div>
              <div className="p-4 bg-white/70 rounded-lg border">
                <p className="text-2xl font-bold">{quiz.max_attempts - (quiz.user_attempts || 0)}</p>
                <p className="text-sm text-muted-foreground">{t('Attempts Left', 'Intentos')}</p>
              </div>
            </div>

            {quiz.user_attempts > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  {t(
                    `You have used ${quiz.user_attempts} of ${quiz.max_attempts} attempts.`,
                    `Has usado ${quiz.user_attempts} de ${quiz.max_attempts} intentos.`
                  )}
                </p>
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                {t(
                  'IMPORTANT: Once you start, the timer begins. Make sure you have 2 hours available and a stable internet connection.',
                  'IMPORTANTE: Una vez que inicies, el temporizador comienza. Asegúrate de tener 2 horas disponibles y una conexión estable a internet.'
                )}
              </p>
            </div>

            <Button 
              size="lg" 
              className="w-full"
              onClick={() => setExamStarted(true)}
            >
              <Play className="h-5 w-5 mr-2" />
              {t('Start Final Exam', 'Iniciar Examen Final')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results screen
  if (showResults && result) {
    const percentage = result.score || ((result.correct_answers / result.total_questions) * 100);
    const passed = result.passed;

    // If not passed, show different message about platform being locked
    if (!passed) {
      return (
        <div className="p-6 max-w-2xl mx-auto">
          <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
            <CardHeader className="text-center">
              <ShieldAlert className="h-20 w-20 mx-auto text-red-500 mb-4" />
              <CardTitle className="text-3xl text-red-700">
                {t('Exam Not Passed', 'Examen No Aprobado')}
              </CardTitle>
              <CardDescription className="text-red-600 text-lg">
                {t('Your training platform has been locked.', 'Tu plataforma de entrenamiento ha sido bloqueada.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-6xl font-bold text-red-600">{percentage.toFixed(0)}%</p>
                <p className="text-muted-foreground text-lg">
                  {result.correct_answers} / {result.total_questions} {t('correct', 'correctas')}
                </p>
                <p className="text-sm text-red-600 mt-2">
                  {t('Required to pass: 80%', 'Requerido para aprobar: 80%')}
                </p>
              </div>

              <Progress value={percentage} className="h-4 [&>div]:bg-red-500" />

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  {t(
                    'Contact your administrator to request an override code. They will review your progress and provide guidance before unlocking the platform.',
                    'Contacta a tu administrador para solicitar un código de desbloqueo. Ellos revisarán tu progreso y te darán orientación antes de desbloquear la plataforma.'
                  )}
                </p>
              </div>

              <Button 
                className="w-full"
                onClick={() => navigate('/training/final-exam')}
              >
                <Lock className="h-4 w-4 mr-2" />
                {t('Go to Unlock Screen', 'Ir a Pantalla de Desbloqueo')}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Passed - show success
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardHeader className="text-center">
            <Trophy className="h-20 w-20 mx-auto text-yellow-500 mb-4" />
            <CardTitle className="text-3xl text-green-700">
              🎉 {t('Congratulations!', '¡Felicidades!')}
            </CardTitle>
            <CardDescription className="text-green-600 text-lg">
              {t('You have passed the Final Certification Exam!', '¡Has aprobado el Examen Final de Certificación!')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-6xl font-bold text-green-600">{percentage.toFixed(0)}%</p>
              <p className="text-muted-foreground text-lg">
                {result.correct_answers} / {result.total_questions} {t('correct', 'correctas')}
              </p>
            </div>

            <Progress value={percentage} className="h-4" />

            <div className="bg-green-100 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800 font-medium">
                🎓 {t('You are now certified! The Sandbox Mode is now unlocked.', '¡Ahora estás certificado! El Modo Sandbox está desbloqueado.')}
              </p>
            </div>

            {/* Download Certificate Button */}
            <Button 
              onClick={handleDownloadCertificate}
              disabled={downloadingCert}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
              size="lg"
              data-testid="download-certificate-btn"
            >
              {downloadingCert ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('Generating...', 'Generando...')}
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  {t('Download Certificate (PDF)', 'Descargar Certificado (PDF)')}
                </>
              )}
            </Button>

            <div className="flex gap-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/training')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('Back to Training', 'Volver a Capacitación')}
              </Button>
              <Button 
                className="flex-1"
                onClick={() => navigate('/training/sandbox')}
              >
                <Play className="h-4 w-4 mr-2" />
                {t('Go to Sandbox', 'Ir al Sandbox')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Exam taking UI
  const questions = quiz.questions || [];
  const question = questions[currentQuestion];
  const progressPercent = ((currentQuestion + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;
  const selectedAnswer = answers[currentQuestion];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button 
          variant="ghost" 
          onClick={() => {
            if (window.confirm(t('Are you sure you want to exit? Your progress will be lost.', '¿Estás seguro que quieres salir? Tu progreso se perderá.'))) {
              navigate('/training');
            }
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Exit Exam', 'Salir del Examen')}
        </Button>
        <Badge variant={timeLeft < 1800 ? "destructive" : "outline"} className="text-lg px-4 py-2">
          <Clock className="h-4 w-4 mr-2" />
          {formatTime(timeLeft)}
        </Badge>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>{t('Question', 'Pregunta')} {currentQuestion + 1} / {questions.length}</span>
          <span>{answeredCount} {t('answered', 'respondidas')}</span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      {/* Question Card */}
      {question && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">
              {lang === 'es' ? question.question_es : question.question_en}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              value={selectedAnswer !== undefined ? selectedAnswer.toString() : undefined} 
              onValueChange={(v) => handleAnswerSelect(parseInt(v))}
            >
              {(lang === 'es' ? question.options_es : question.options_en).map((option, index) => (
                <div 
                  key={index} 
                  className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedAnswer === index 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleAnswerSelect(index)}
                >
                  <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                  <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>
      )}

      {/* Question Navigation Grid */}
      <Card className="mb-6">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">
            {t('Question Navigator', 'Navegador de Preguntas')}
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-2">
            {questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                  idx === currentQuestion
                    ? 'bg-primary text-primary-foreground'
                    : answers[idx] !== undefined
                      ? 'bg-green-500 text-white'
                      : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={handlePrevQuestion}
          disabled={currentQuestion === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Previous', 'Anterior')}
        </Button>
        
        {currentQuestion < questions.length - 1 ? (
          <Button onClick={handleNextQuestion}>
            {t('Next', 'Siguiente')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? (
              <>{t('Submitting...', 'Enviando...')}</>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('Submit Exam', 'Enviar Examen')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
